/**
 * Concepts — the gateable concept registry (Phase 2). A Concept is the unit of
 * gating: a small per-subject list of advanced methods/techniques. Pick a subject,
 * then create/edit concepts (name, aliases, the topic that introduces it, gateable
 * flag). Grade is NOT set here — it comes from a concept-level CurriculumPlacement
 * or is inherited from the introducing topic.
 *
 * Phase 2 of docs/FeatureDesigns/modules-curriculum-topics-concepts/concept-curriculum-model-implplan.md.
 * Modeled on CurriculumBuilder.tsx (react-query + DataTable/Modal patterns).
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api/adminApi';
import type { Concept, Module, SubjectType } from '../types';
import { SUBJECT_OPTIONS } from '@padcanvas/shared';
import {
  DataTable, type ColumnDef, Modal, Input, Select, Button, SearchFilterBar, type FilterDef, InfoTip,
} from '@shared-ui/components';
import { TopicSelector } from '@shared-ui/components/TopicSelector';
import { ConceptHierarchy } from './ConceptHierarchy';
import { ConceptGraphStats } from './ConceptGraphStats';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const READABLE_ID = /^[a-z0-9_-]+$/;

const toggleBtn: React.CSSProperties = {
  border: 'none', padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
};

export function Concepts() {
  const qc = useQueryClient();
  // Filters in the URL so returning from a concept detail restores them.
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = { subject: searchParams.get('subject') ?? 'maths' };
    searchParams.forEach((v, k) => { if (k !== 'subject' && v) out[k] = v; });
    return out;
  }, [searchParams]);
  const setFilters = (next: Record<string, string>) =>
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      const keys = new Set([...Object.keys(next), ...Array.from(prev.keys())]);
      for (const k of keys) { const v = next[k]; if (v) p.set(k, v); else p.delete(k); }
      return p;
    }, { replace: true });
  const subject = (filters.subject || 'maths') as SubjectType;
  const [modal, setModal] = useState<Partial<Concept> | null>(null);
  const [aliasesText, setAliasesText] = useState('');
  const view = (filters.view === 'table' ? 'table' : filters.view === 'stats' ? 'stats' : 'tree') as 'tree' | 'table' | 'stats';

  // ── Queries ──
  const { data: conceptsData } = useQuery({
    queryKey: ['concepts', subject],
    queryFn: () => adminApi.listConcepts({ subject }),
    enabled: !!subject,
  });
  const concepts = conceptsData?.concepts ?? [];

  const { data: modulesData } = useQuery({
    queryKey: ['modules', subject],
    queryFn: () => adminApi.getModules({ subject }),
    enabled: !!subject,
  });
  const modules = modulesData?.modules ?? [];

  // Problem types for the hierarchy view (nested under their concept). Fetched only
  // in tree view; the endpoint returns lean summaries (no member arrays).
  const { data: problemTypesData } = useQuery({
    queryKey: ['problem-types', subject],
    queryFn: () => adminApi.listProblemTypes(subject),
    enabled: !!subject && view === 'tree',
  });
  const problemTypes = problemTypesData?.problemTypes ?? [];

  // Type-level navigation edges (harder/easier-variant, prerequisite, follow-up,
  // similar-concept) overlaid on each type in the tree. Tree view only.
  const { data: relationsData } = useQuery({
    queryKey: ['problem-type-relations', subject],
    queryFn: () => adminApi.listProblemTypeRelations(subject),
    enabled: !!subject && view === 'tree',
  });
  const problemTypeRelations = relationsData?.relations ?? [];

  // Modules for the concept being edited — its subject can differ from the page
  // filter, so the topic selector must reflect the modal's own subject. Same
  // query key as above, so it reuses the cache when the subjects match.
  const modalSubject = modal?.subject as SubjectType | undefined;
  const { data: modalModulesData } = useQuery({
    queryKey: ['modules', modalSubject],
    queryFn: () => adminApi.getModules({ subject: modalSubject! }),
    enabled: !!modalSubject,
  });
  const modalModules = modalModulesData?.modules ?? [];

  // Topic-level modules (the leaves) are the valid "introduced at" targets.
  // Build a "Chapter › Topic" label for each, sorted for the picker.
  const topicOptions = useMemo(() => {
    const byId = new Map(modules.map(m => [m.id, m]));
    const level = (m: Module) => m.moduleLevel ?? (m.isTopModule ? 'module' : 'chapter');
    return modules
      .filter(m => level(m) === 'topic')
      .map(m => {
        const parent = m.parentModuleId ? byId.get(m.parentModuleId) : undefined;
        return { id: m.id, label: parent ? `${parent.displayName} › ${m.displayName}` : m.displayName };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [modules]);
  const topicLabelById = useMemo(
    () => new Map(topicOptions.map(t => [t.id, t.label])),
    [topicOptions],
  );

  // Modules in the shared TopicSelector's ModuleItem shape (searchable picker),
  // built from the modal subject's modules so chapters/topics match the subject.
  const selectorModules = useMemo(() => modalModules.map(m => ({
    id: m.id, displayName: m.displayName, subject: m.subject,
    isTopModule: m.isTopModule ?? (m.moduleLevel === 'module'),
    moduleLevel: m.moduleLevel ?? 'chapter', parentModuleId: m.parentModuleId,
    sortOrder: m.sortOrder ?? 0, grades: m.grades,
  })), [modalModules]);

  // ── Mutations ──
  const saveConcept = useMutation({
    mutationFn: (c: Partial<Concept>) => adminApi.saveConcept(c),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['concepts', subject] }); setModal(null); },
  });
  const deleteConcept = useMutation({
    mutationFn: (id: string) => adminApi.deleteConcept(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['concepts', subject] }); setModal(null); },
  });

  // ── Filter rows ──
  const visibleConcepts = useMemo(() => {
    let r = concepts;
    if (filters.gateable === 'gateable') r = r.filter(c => c.gateable !== false);
    if (filters.gateable === 'non-gateable') r = r.filter(c => c.gateable === false);
    const q = (filters.search || '').trim().toLowerCase();
    if (q) r = r.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.aliases?.some(a => a.toLowerCase().includes(q)),
    );
    return [...r].sort((a, b) => a.name.localeCompare(b.name));
  }, [concepts, filters]);

  const gateableCount = concepts.filter(c => c.gateable !== false).length;

  const columns: ColumnDef<Concept>[] = [
    {
      key: 'name', header: 'Concept', sortable: false,
      render: (c) => (
        <div>
          <div style={{ fontWeight: 600 }}>{c.name}</div>
          {c.aliases?.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>aka {c.aliases.join(', ')}</div>
          )}
        </div>
      ),
    },
    {
      key: 'introducedAt', header: 'Introduced at',
      render: (c) => c.introducedAtTopicId
        ? <span>{topicLabelById.get(c.introducedAtTopicId) ?? <em style={{ color: 'var(--text-secondary)' }}>{c.introducedAtTopicId} (unknown)</em>}</span>
        : <span style={{ color: 'var(--text-secondary)' }}>—</span>,
    },
    {
      key: 'gateable', header: 'Gateable',
      render: (c) => c.gateable !== false
        ? <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>gateable</span>
        : <span style={{ color: 'var(--text-secondary)' }}>basic</span>,
    },
    {
      key: 'actions', header: '',
      render: (c) => (
        <Button variant="secondary" size="sm" onClick={() => openEdit(c)}>Edit</Button>
      ),
    },
  ];

  function openNew() {
    setAliasesText('');
    setModal({ subject, gateable: true, aliases: [] });
  }
  function openEdit(c: Concept) {
    setAliasesText((c.aliases ?? []).join(', '));
    setModal({ ...c });
  }

  function commitSave() {
    if (!modal) return;
    const aliases = aliasesText.split(',').map(s => s.trim()).filter(Boolean);
    saveConcept.mutate({ ...modal, subject: modal.subject ?? subject, aliases });
  }

  const filterDefs: FilterDef[] = [
    { key: 'subject', kind: 'select', required: true, placeholder: 'Subject', options: SUBJECT_OPTIONS.map(s => ({ value: s, label: capitalize(s) })) },
    { key: 'gateable', placeholder: 'All concepts', options: [{ value: 'gateable', label: 'Gateable' }, { value: 'non-gateable', label: 'Basic' }] },
  ];

  const isExisting = !!modal && concepts.some(c => c.id === modal.id);

  return (
    <div className="settings-section">
      <div className="section-header">
        <div className="section-header-top">
          <h2>Concepts <InfoTip text="Gateable concepts — advanced methods (differentiation, dot product…) checked before hints use them. Keep it small; basics aren't tracked. Grade comes from curriculum placement, not here." /></h2>
        </div>
      </div>

      <SearchFilterBar
        filters={filterDefs}
        values={filters}
        onChange={setFilters}
        searchPlaceholder="Search concept / alias…"
        hideFetch
        trailingActions={
          <>
            <div style={{ display: 'inline-flex', border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden' }}>
              <button onClick={() => setFilters({ ...filters, view: 'tree' })}
                style={{ ...toggleBtn, background: view === 'tree' ? 'var(--color-primary)' : 'transparent', color: view === 'tree' ? '#fff' : 'inherit' }}>Hierarchy</button>
              <button onClick={() => setFilters({ ...filters, view: 'table' })}
                style={{ ...toggleBtn, background: view === 'table' ? 'var(--color-primary)' : 'transparent', color: view === 'table' ? '#fff' : 'inherit' }}>Table</button>
              <button onClick={() => setFilters({ ...filters, view: 'stats' })}
                style={{ ...toggleBtn, background: view === 'stats' ? 'var(--color-primary)' : 'transparent', color: view === 'stats' ? '#fff' : 'inherit' }}>Graph stats</button>
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{gateableCount} gateable · {concepts.length} total</span>
            <Button variant="primary" size="sm" onClick={openNew}>+ New Concept</Button>
          </>
        }
      />
      {view === 'stats' ? (
        <ConceptGraphStats subject={subject} />
      ) : view === 'tree' ? (
        <ConceptHierarchy concepts={concepts} problemTypes={problemTypes} relations={problemTypeRelations} onEdit={openEdit} />
      ) : (
        <DataTable
          columns={columns}
          data={visibleConcepts}
          rowKey={(c) => c.id}
          onRowClick={(c) => openEdit(c)}
          emptyMessage="No concepts for this subject yet"
          compact
          bordered
        />
      )}

      {/* Create / edit modal */}
      <Modal isOpen={!!modal} onClose={() => setModal(null)} title={isExisting ? 'Edit Concept' : 'New Concept'} size="md">
        {modal && (
          <div style={{ display: 'grid', gap: 12 }}>
            <label>Subject
              <Select value={modal.subject ?? subject} disabled={isExisting}
                onChange={(e) => setModal({ ...modal, subject: e.target.value as SubjectType, introducedAtTopicId: undefined })}>
                {SUBJECT_OPTIONS.map(s => <option key={s} value={s}>{capitalize(s)}</option>)}
              </Select>
            </label>
            <label>Id (stable key, e.g. <code>dot-product</code>)
              <Input value={modal.id ?? ''} disabled={isExisting}
                onChange={(e) => setModal({ ...modal, id: e.target.value })} placeholder="dot-product" />
            </label>
            <label>Name
              <Input value={modal.name ?? ''} onChange={(e) => setModal({ ...modal, name: e.target.value })} placeholder="Dot product" />
            </label>
            <label>Aliases (comma-separated — power text auto-suggest)
              <Input value={aliasesText} onChange={(e) => setAliasesText(e.target.value)} placeholder="scalar product, inner product" />
            </label>
            {/* NOT a <label>: its options are plain divs, so a click inside a
                label is forwarded to the label's control instead of selecting
                — in every browser, not just WebKit. */}
            <div className="field">
              <span className="field-label">Introduced at (topic that first teaches it)</span>
              <TopicSelector
                modules={selectorModules}
                value={modal.introducedAtTopicId ?? ''}
                placeholder="Search module / chapter / topic…"
                onChange={(id) => setModal({ ...modal, introducedAtTopicId: id || undefined })}
              />
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={modal.gateable !== false}
                onChange={(e) => setModal({ ...modal, gateable: e.target.checked })} />
              Gateable (check before a hint uses it; uncheck for universal basics)
            </label>
            <label>Description (optional)
              <Input value={modal.description ?? ''} onChange={(e) => setModal({ ...modal, description: e.target.value })} />
            </label>
            {(saveConcept.error || deleteConcept.error) ? (
              <div style={{ color: 'var(--color-error)', fontSize: 13 }}>
                {((saveConcept.error || deleteConcept.error) as Error).message}
              </div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <Button variant="danger" size="sm" disabled={!isExisting || deleteConcept.isPending}
                onClick={() => { if (modal.id && confirm(`Delete concept "${modal.name}"?`)) deleteConcept.mutate(modal.id); }}>Remove</Button>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="secondary" size="sm" onClick={() => setModal(null)}>Cancel</Button>
                <Button variant="primary" size="sm"
                  disabled={!modal.id || !modal.name || !READABLE_ID.test(modal.id ?? '') || saveConcept.isPending}
                  onClick={commitSave}>Save</Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
